import classNames from 'classnames';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Slider, { CustomArrowProps, Settings } from 'react-slick';

import InlineSpinner from '../../../common/components/misc/InlineSpinner';
import { CardDetailsDTO } from '../../../common/types/dto/CardDetailsDTO';
import { TranslationFuncProp } from '../../../common/types/prop-types/common';
import caixoteLixo from '../../assets/images/icons/Cinzentos/caixote-lixo.svg';
import orbitDireita from '../../assets/images/icons/Cinzentos/setaOrbitDireita.svg';
import orbitEsquerda from '../../assets/images/icons/Cinzentos/setaOrbitEsquerda.svg';
import sinalMais from '../../assets/images/icons/Cinzentos/sinalMais.svg';
import css from './Cards.scss';
import CardTemplate from './CardTemplate';
import CardsContainer, { CardsProps } from './containers/CardsContainer';
import CustomerInfo from './CustomerInfo';
import DeleteCard from './DeleteCard';
import NewCard from './NewCard';

interface CardsRenderProps {
    renderCustomerInfo: boolean;
    renderButtons: boolean;
}

interface CardButtonEvents {
    onNew: React.Dispatch<React.SetStateAction<boolean>>;
    onRemove: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoadingCards: React.FC<CardsProps & TranslationFuncProp> = (props) => {
    if (!props.cards) {
        return (
            <div className={classNames(['orbit', 'os-meus-cartoes', 'margin-bottom-1'])}>
                <div className={classNames([css.card, css.loading])}>
                    <div className={css.verticalAlignChild}>
                        <InlineSpinner size={'100px'} />
                        <h4 id="loading-card-text">{props.t('loadingText')}</h4>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

const NoCards: React.FC<CardsProps & TranslationFuncProp> = (props) => {
    if (props.cards && props.cards.length === 0) {
        return (
            <div className={classNames(['orbit', 'os-meus-cartoes', 'margin-bottom-1'])}>
                <figure className={'orbit-figure'}>
                    <div className={'card'}>
                        <div className={classNames(['card-section', 'text-center'])}>
                            <h4>{props.t('cards:layout.noCards')}</h4>
                            <p>{props.t(`cards:customerInfo.noCardsSub`)}</p>
                        </div>
                    </div>
                </figure>
            </div>
        );
    }
    return null;
};

function buildCards(cards: CardDetailsDTO[], t: TFunction) {
    return cards.map((cardItem: CardDetailsDTO) => {
        return (
            <React.Fragment key="cardItem">
                <CardTemplate
                    type={t(`cards:newCard.defaultType`)}
                    state={t('cards:card.state.' + cardItem.state)}
                    number={cardItem.number}
                    dealer={cardItem.dealer}
                    licensePlate={cardItem.licensePlate}
                    vin={cardItem.vin}
                    validDate={cardItem.validDate}
                />
            </React.Fragment>
        );
    });
}

const PrevArrow = (props: CustomArrowProps) => {
    const { onClick } = props;
    return (
        <button type="button" onClick={onClick} data-role="none" className={classNames(['orbit-previous', css.miniArrowSlickPrev])}>
            <img src={orbitEsquerda} height="13" />
        </button>
    );
};

const NextArrow = (props: CustomArrowProps) => {
    const { onClick } = props;
    return (
        <button type="button" onClick={onClick} data-role="none" className={classNames(['orbit-next', css.miniArrowSlickNext])}>
            <img src={orbitDireita} height="13" />
        </button>
    );
};

const DisplayCards: React.FC<CardsProps & TranslationFuncProp> = (props) => {
    if (props.cards && props.cards.length > 0) {
        const settings: Settings = {
            dots: false,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            nextArrow: <NextArrow />,
            prevArrow: <PrevArrow />,
        };
        return (
            <div className={classNames(['orbit', 'os-meus-cartoes', 'margin-bottom-1'])}>
                {props.cards.length > 1 ?
                    <Slider {...settings}>
                        {buildCards(props.cards, props.t)}
                    </Slider>
                    :
                    buildCards(props.cards, props.t)
                }
            </div>
        );
    }
    return null;
};

const HandleAddRemoveBtns: React.FC<CardsProps & TranslationFuncProp & CardButtonEvents> = (props) => {
    if (props.cards) {
        const showDeleted: boolean = props.cards.length > 0;
        return (
            <React.Fragment>
                <div className={classNames(['button-group', 'margin-0'])}>
                    <button key="leftBtn" onClick={() => { props.onNew(true); }} className={classNames(['button', 'button-svg-text'])}>
                        <img src={sinalMais} />
                        <span>{props.t('cards:layout.newCard')}</span>
                    </button>
                    {showDeleted &&
                        <button key="rightBtn" onClick={() => { props.onRemove(true); }} className={classNames(['button', 'button-svg-text'])}>
                            <img src={caixoteLixo} />
                            <span>{props.t('cards:layout.deleteCard')}</span>
                        </button>
                    }
                </div>
            </React.Fragment>
        );
    }
    return null;
};

const CardSlider: React.FC<CardsProps> = (props) => {
    const { t } = useTranslation();
    return (
        <React.Fragment>
            <h4>{t('cards:layout.myCards')}</h4>
            <LoadingCards
                t={t}
                {...props}
            />
            <NoCards
                t={t}
                {...props}
            />
            <DisplayCards
                t={t}
                {...props}
            />
        </React.Fragment>
    );
};

const Cards: React.FC<CardsRenderProps> = (componentProps) => {
    const { t } = useTranslation();
    const [isAddingCard, setIsAddingCard] = useState(false);
    const [isRemovingCard, setIsRemovingCard] = useState(false);
    return (
        <CardsContainer t={t} >
            {(props: CardsProps) => {
                if (componentProps.renderCustomerInfo) {
                    return (
                        <React.Fragment>
                            <div className={classNames(['row', 'medium-unstack', 'dados-utilizador', 'grid-x'])}>
                                <div className={classNames(['columns', 'medium-6', 'small-12'])}>
                                    <CustomerInfo />
                                </div>
                                <div className={classNames(['columns', 'medium-6', 'small-12'])}>
                                    {isAddingCard && <NewCard onCancel={() => { setIsAddingCard(false); }} onSubmit={() => { setIsAddingCard(false); }} />}
                                    {isRemovingCard && <DeleteCard onCancel={() => { setIsRemovingCard(false); }} onSubmit={() => { setIsRemovingCard(false); }} />}
                                    {!isAddingCard && !isRemovingCard && <CardSlider {...props} />}
                                </div>
                            </div>
                            {componentProps.renderButtons && !isAddingCard && !isRemovingCard &&
                                <div className={classNames(['row', 'medium-unstack', 'align-middle', 'dados-utilizador-comandos', 'grid-x'])}>
                                    <div className={classNames(['columns', 'medium-6', 'small-12'])} />
                                    <div className={classNames(['columns', 'medium-6', 'small-12', 'dados-utilizador-comandos-adicionar'])}>
                                        <HandleAddRemoveBtns t={t} {...props} onNew={setIsAddingCard} onRemove={setIsRemovingCard} />
                                    </div>
                                </div>
                            }
                        </React.Fragment>
                    );
                } else {
                    return (
                        <React.Fragment>
                            {isAddingCard && <NewCard onCancel={() => { setIsAddingCard(false); }} onSubmit={() => { setIsAddingCard(false); }} />}
                            {isRemovingCard && <DeleteCard onCancel={() => { setIsRemovingCard(false); }} onSubmit={() => { setIsRemovingCard(false); }} />}
                            {!isAddingCard && !isRemovingCard &&
                                <React.Fragment>
                                    <CardSlider {...props} />
                                    {componentProps.renderButtons && <HandleAddRemoveBtns t={t} {...props} onNew={setIsAddingCard} onRemove={setIsRemovingCard} />}
                                </React.Fragment>
                            }
                        </React.Fragment>
                    );
                }
            }}
        </CardsContainer>
    );
};

export default Cards;
